import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { PreviewAssets } from 'src/types';
import { loadImage } from 'src/utils/preview/canvasUtils';
import {
  renderImage,
  renderImageCenterCrop,
  renderImageResize,
  renderImageFixed,
  renderImageFitWithin,
  renderGradient,
  renderText,
} from 'src/utils/preview/layerRenderers';
import {
  PreviewStateConfig,
  LayerType,
  BackgroundLayer,
  VideoLayer,
  LogoLayer,
  TextLayer,
  BackgroundTransformType,
  LogoTransformType,
} from 'src/utils/preview/types';

/**
 * Single unified preview renderer
 *
 * Orchestrates rendering of all layer types for preview generation.
 * Device-specific behavior is driven by configuration, not code.
 *
 * Rendering order follows layer array order in config:
 * 1. Background/Video layers (base image)
 * 2. Gradient overlays
 * 3. Logo layers
 * 4. Text layers
 * 5. Shell overlays (device frame)
 *
 * Each layer is rendered sequentially. If any layer fails to render,
 * an error is thrown and rendering stops immediately.
 *
 * @param ctx - Canvas rendering context to draw on
 * @param assets - Preview assets (images, text, video URLs)
 * @param config - Preview state configuration with layers and dimensions
 * @throws {Error} If layer rendering fails or unknown layer type encountered
 *
 */
export const previewRenderer = async (
  ctx: CanvasRenderingContext2D,
  assets: PreviewAssets,
  config: PreviewStateConfig,
): Promise<void> => {
  for (const layer of config.layers) {
    switch (layer.type) {
      case LayerType.BACKGROUND:
        await renderBackgroundLayer(ctx, layer, assets);
        break;
      case LayerType.VIDEO:
        await renderVideoLayer(ctx, layer, assets, ctx.canvas.width, ctx.canvas.height);
        break;
      case LayerType.GRADIENT:
        renderGradient(ctx, layer, config);
        break;
      case LayerType.LOGO:
        await renderLogoLayer(ctx, layer, assets, config);
        break;
      case LayerType.TEXT:
        renderText(ctx, layer, assets);
        break;
      case LayerType.SHELL:
        await renderImage(ctx, layer);
        break;
      default:
        throw new Error(`Unknown layer type: ${(layer as { type: string }).type}`);
    }
  }
};

/**
 * Render background image layer
 *
 * @param ctx - Canvas rendering context to draw on
 * @param layer - Background layer configuration with transform
 * @param assets - Preview assets containing background image URL
 * @throws {Error} If background image URL is missing or image fails to load
 */
const renderBackgroundLayer = async (
  ctx: CanvasRenderingContext2D,
  layer: BackgroundLayer,
  assets: PreviewAssets,
): Promise<void> => {
  const bgUrl = assets[AssetType.BACKGROUND_IMAGE];
  if (!bgUrl) throw new Error('Background image URL is missing');

  const bgImage = await loadImage(bgUrl);

  if (layer.transform.type === BackgroundTransformType.RESIZE) {
    await renderImageResize(ctx, bgImage, layer.transform);
  } else if (layer.transform.type === BackgroundTransformType.CENTER_CROP) {
    await renderImageCenterCrop(
      ctx,
      bgImage,
      layer.transform.width ?? ctx.canvas.width,
      layer.transform.height ?? ctx.canvas.height,
      layer.transform.x ?? 0,
      layer.transform.y ?? 0,
      layer.transform.shiftFactor ?? 0.5,
    );
  }
};

/**
 * Render logo image layer
 *
 * @param ctx - Canvas rendering context to draw on
 * @param layer - Logo layer configuration with transform
 * @param assets - Preview assets containing logo image URL
 * @param config - Preview state config for container dimensions
 */
const renderLogoLayer = async (
  ctx: CanvasRenderingContext2D,
  layer: LogoLayer,
  assets: PreviewAssets,
  config: PreviewStateConfig,
): Promise<void> => {
  const logoUrl = assets[AssetType.LOGO];
  if (!logoUrl) throw new Error('Logo URL is missing');

  const logoImage = await loadImage(logoUrl);

  if (layer.transform.type === LogoTransformType.FIT_WITHIN) {
    let transform = layer.transform;

    // When body text is absent, vertically center the logo in the combined logo+text content area
    if (!assets.bodyCopy) {
      const textLayer = config.layers.find((l): l is TextLayer => l.type === LayerType.TEXT);
      if (textLayer) {
        const logoY = transform.position.y;
        const adjustedY = logoY + (textLayer.position.y - logoY) / 2;
        transform = { ...transform, position: { ...transform.position, y: adjustedY } };
      }
    }

    renderImageFitWithin(ctx, logoImage, transform);
  } else if (layer.transform.type === LogoTransformType.FIXED) {
    renderImageFixed(
      ctx,
      logoImage,
      layer.transform.x,
      layer.transform.y,
      layer.transform.width,
      layer.transform.height,
    );
  }
};

/**
 * Render video trailer layer
 *
 * @param ctx - Canvas rendering context to draw on
 * @param layer - Video layer configuration with transform
 * @param assets - Preview assets containing video trailer URL
 * @param canvasWidth - Canvas width for default crop dimensions
 * @param canvasHeight - Canvas height for default crop dimensions
 * @throws {Error} If video trailer URL is missing or image fails to load
 */
const renderVideoLayer = async (
  ctx: CanvasRenderingContext2D,
  layer: VideoLayer,
  assets: PreviewAssets,
  canvasWidth: number,
  canvasHeight: number,
): Promise<void> => {
  const videoUrl = assets[AssetType.VIDEO_TRAILER];
  if (!videoUrl) throw new Error('Video trailer URL is missing');

  const videoImage = await loadImage(videoUrl);

  if (layer.transform.type === BackgroundTransformType.RESIZE) {
    await renderImageResize(ctx, videoImage, layer.transform);
  } else if (layer.transform.type === BackgroundTransformType.CENTER_CROP) {
    await renderImageCenterCrop(
      ctx,
      videoImage,
      canvasWidth,
      canvasHeight,
      layer.transform.x ?? 0,
      layer.transform.y ?? 0,
      layer.transform.shiftFactor ?? 0.5,
    );
  }
};
